import { getAssociatedTokenAddress } from '@solana/spl-token';
import { useConnection } from '@solana/wallet-adapter-react';
import { PublicKey } from '@solana/web3.js';
import { useCallback } from 'react';
import { toast } from 'sonner';
import {
  encodeAbiParameters,
  EncodeAbiParametersReturnType,
  keccak256,
} from 'viem';
import { signMessage } from 'viem/accounts';

// eslint-disable-next-line import/no-cycle
import { TransferFormValues } from '@/components/header/modals/transfer-dialog';
import { useFetchAccount } from '@/features/account/api/get-account';
import { getTransfers } from '@/features/account/api/get-transfers';
import { usePrivyAccount } from '@/hooks';
import { useAccountStore } from '@/store/use-account-store';
import { COLLATERAL_DECIMALS } from '@/store/use-markets-store';
import { useTransfersStore } from '@/store/use-transfers-store';
import { ErrorCode, ErrorMsgs, OrderStatus, EventType } from '@/types';
import { sendEvent } from '@/utils/analytics';
import {
  handleTransferForStore,
  transferResponseToTransfer,
} from '@/utils/handle-store-data';
import { solanaAddressToHex } from '@/utils/solana';
import { bigIntToNum, parseDecimalToBigInt } from '@/utils/value-format';

import { privateApi } from '../api-clients/rest-client';
import { USDC_MINT_ADDRESS } from '../wagmi/config';

export const useSolanaWithdraw = (execute = false) => {
  const { connection } = useConnection();
  const { data: account } = useFetchAccount();

  const address = useAccountStore((state) => state.address);

  const { email } = usePrivyAccount();

  const withdraw = useCallback(
    async (data: TransferFormValues) => {
      if (!account?.address) {
        toast.error('Wallet address not found. Please reconnect your wallet.');
        return;
      }

      if (!data.recipient) {
        toast.error('Invalid recipient address.');
        return;
      }

      const { getAuthParams, getAccurateTime, nextNonce } =
        useAccountStore.getState();

      const authParams = getAuthParams();

      if (!authParams?.signingKey) {
        console.error('No signing key found');
        throw new Error('No signing key found');
      }

      const time = getAccurateTime();
      const nonce = nextNonce();

      const userTokenAccount = await getAssociatedTokenAddress(
        new PublicKey(USDC_MINT_ADDRESS),
        new PublicKey(data.recipient),
      );

      const size = parseDecimalToBigInt(
        data.size.endsWith('.') ? `${data.size}00` : data.size,
        COLLATERAL_DECIMALS,
      );

      let encodedOrder: EncodeAbiParametersReturnType;

      try {
        encodedOrder = encodeAbiParameters(
          [
            { name: 'time', type: 'uint256' },
            { name: 'nonce', type: 'uint256' },
            { name: 'isDeposit', type: 'bool' },
            { name: 'account', type: 'address' },
            { name: 'recipient', type: 'bytes32' },
            { name: 'token', type: 'address' },
            { name: 'size', type: 'uint256' },
            { name: 'chainId', type: 'uint256' },
          ],
          [
            BigInt(time),
            BigInt(nonce),
            false,
            account.address as `0x${string}`,
            solanaAddressToHex(userTokenAccount.toString()) as `0x${string}`,
            '0xaf88d065e77c8cc2239327c5edb3a432268e5831' as `0x${string}`,
            BigInt(size),
            BigInt(0),
          ],
        );
      } catch (err) {
        toast.error(
          'message' in (err as Error)
            ? (err as Error).message.split('.')[0] + '.'
            : 'Something went wrong. Please check your input and try again.',
        );
        return;
      }

      const signature = await signMessage({
        message: { raw: keccak256(encodedOrder) },
        privateKey: authParams.signingKey,
      });

      const body = {
        order: {
          deposit: false,
          size: Number(size),
          account: account.address,
          nonce,
          time,
          token: '0xaf88d065e77c8cc2239327c5edb3a432268e5831',
          recipient: userTokenAccount.toString(),
          chainId: 0,
        },
        execute,
        signature,
      };

      const shouldWaitValidation = !execute && Number(data.size) < 50000;

      const promise = privateApi.post('/transfer/withdraw', body);

      const toastPromise = promise.then((res) => {
        const { id: orderId } = res;
        sendEvent(EventType.WITHDRAW_INITIATED, {
          amount: bigIntToNum(size, COLLATERAL_DECIMALS),
          email,
        });
        return new Promise((resolve, reject) => {
          // eslint-disable-next-line prefer-const
          let subscriberTimeoutId: NodeJS.Timeout;
          // eslint-disable-next-line prefer-const
          let pollIntervalId: NodeJS.Timeout;
          // eslint-disable-next-line prefer-const
          let unsubscribe: () => void;

          const cleanup = () => {
            clearTimeout(subscriberTimeoutId);
            clearInterval(pollIntervalId);
            unsubscribe();
          };

          unsubscribe = useTransfersStore.subscribe((state, _prevState) => {
            const transfers = address ? state.transfers[address] ?? {} : {};
            if (orderId in transfers) {
              cleanup();
              if (
                transfers[orderId].status === OrderStatus.NEW ||
                transfers[orderId].status === OrderStatus.PROCESSING
              ) {
                if (shouldWaitValidation)
                  toast.loading('Waiting for order validation...');
                resolve(transfers[orderId]);
              } else if (transfers[orderId].status === OrderStatus.REJECTED) {
                reject(transfers[orderId].code as ErrorCode);
              }
            }
          });

          subscriberTimeoutId = setTimeout(() => {
            cleanup();
            reject(ErrorCode.INTERNAL_CREATE_ORDER_TIMEOUT);
          }, 10_000); // 10 seconds timeout

          pollIntervalId = setInterval(() => {
            getTransfers({ id: orderId }).then((res) =>
              res.forEach((transfer) =>
                handleTransferForStore(transferResponseToTransfer(transfer)),
              ),
            );
          }, 2_000);
        });
      });

      toast.promise(toastPromise, {
        loading: 'Requesting withdrawal...',
        success: 'Requested withdrawal successfully',
        error: (error) => {
          return `Withdraw failed: ${ErrorMsgs[error as ErrorCode]}`;
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [connection, account, execute, email],
  );

  return { withdraw };
};
